import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Prefetch data with React Query and NextJS – CodeWithSwiz 8, 9",
  "description": "Prefetching lets you start React apps without loading spinners. Gatsby introduced this concept with `staticQuery`, react-query and NextJS make the pattern easier 😍",
  "published": "2020-09-30T00:00:00.000Z",
  "categories": "CodeWithSwiz, Technical, NextJS, Livecoding",
  "hero": "./img/screenshot-1601482922767.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Prefetching lets you start React apps without loading spinners. Gatsby introduced this concept with `}<inlineCode parentName="p">{`staticQuery`}</inlineCode>{`, react-query and NextJS make the pattern easier 😍`}</p>
    <p><em parentName="p">{`CodeWithSwiz is a twice-a-week live show. Like a podcast with video and fun hacking. Focused on experiments. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Wednesdays and Sundays`}</a></em></p>
    <p>{`It took two episodes to figure this out and `}<em parentName="p">{`verify`}</em>{` it works. Here they are 👇`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=_t66WKiX6Z4"
      }}>{`https://www.youtube.com/watch?v=`}{`_`}{`t66WKiX6Z4`}</a></p>
    <lite-youtube {...{
      "videoid": "20FD0Czbatw",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`We're building towards a headless CMS. The component that needs prefetching is a social card.`}</p>
    <h2 {...{
      "id": "prefetchable-component"
    }}>{`Prefetchable component`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export const SocialCardImage = ({ title }) => {
  const cardQuery = useSocialCardQuery(title)

  if (cardQuery.isLoading) {
    return <Spinner />
  }

  return <Image src={cardQuery.data.url} />
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`SocialCardImage`}</inlineCode>{` component uses a query to create a social card for a title. When the query `}<inlineCode parentName="p">{`isLoading`}</inlineCode>{`, we show a spinner, when it's done, we show the image.`}</p>
    <p><img parentName="p" {...{
        "src": "/d829b5cf78881ab9e634d9df7506cb31/Component-with-spinner5d1ibg.gif",
        "alt": "Component with spinner"
      }}></img></p>
    <p>{`We built the `}<inlineCode parentName="p">{`useSocialQuery`}</inlineCode>{` custom hook `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/building-a-small-cms-with-nextjs-pt2-codewithswiz/"
      }}>{`in a past CodeWithSwiz episode`}</a>{`. A thin wrapper on react-query's `}<inlineCode parentName="p">{`useQuery`}</inlineCode>{` makes it more reusable.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`export function useSocialCardQuery(title) {
  return useQuery(["social-card", title], fetchSocialCard, {
    staleTime: 5000,
  })
}
`}</code></pre>
    <p>{`Name the query `}<inlineCode parentName="p">{`"social-card"`}</inlineCode>{`, add `}<inlineCode parentName="p">{`title`}</inlineCode>{` as a parameter, use the `}<inlineCode parentName="p">{`fetchSocialCard`}</inlineCode>{` data loader, and set `}<inlineCode parentName="p">{`staleTime`}</inlineCode>{` to 5 seconds. Helps with query reuse and avoids refetching.`}</p>
    <p>{`The data loader talks to my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/my-favorite-serverless-project/"
      }}>{`screenshot AWS Lambda`}</a>{` to create social cards.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function fetchSocialCard(key, title) {
  if (title) {
    const res = await fetch(
      \`https://pifc233qp6.execute-api.us-east-1.amazonaws.com/dev/social-card?title=\${title}\`
    )

    return res.json()
  } else {
    return null
  }
}
`}</code></pre>
    <p>{`Basic `}<inlineCode parentName="p">{`fetch()`}</inlineCode>{` request to a lambda. The lambda spins up a Chrome browser, loads a special page on my blog, renders the card, takes a screenshot, saves it to S3, returns a URL.`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/my-favorite-serverless-project/"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "800px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "68.60986547085201%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'275\\'%20viewBox=\\'0%200%20400%20275\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M169%2024v41c1%202%202-4%202-21V24h9l29%204-3%2031c0%203%200%203-6%203a159%20159%200%2000-26%202c-5%201-5%201-3%202h11l17-2c7%200%207%200%207%203-1%203%201%202%202-2l1-4%201-18a2165%202165%200%2000-1-18l-11-1c-13-3-29-3-29%200m-67%2019c-19%203-49%203-60%200h-7c0%204%2046%205%2072%200%204%200%204%200%204%202%200%203-1%204-9%207a225%20225%200%2000-27%2011v1c1%201%204%200%2012-4%2012-6%2020-9%2023-9%202%200%201%2013-2%2037-2%2018-4%2041-2%2041l1-16a382%20382%200%20015-54v-9l4-1%2017-3%2016-2a351%20351%200%2001-36%202v-4h-2l-9%201m-72%204v9a767%20767%200%20011%2086l8-2%2041-2c39-1%2035-1%2035-2l-3-1-34%202c-25%200-37%201-44%203l-1-36c0-50-1-64-3-57m166%2030c-2%200-3%202-3%202l-1-1c-1-2-1-2-2-1l2%206%202%208%201%203%202%203%203%208c1%203%201%203%201%201-1-3%200-3%202-4%204%200%205-2%202-2l-2-1-2-1-2%201a253%20253%200%2000-6-18l4%201c5%200%208-1%208-3h-4l-4%201c-2%200-2%200-1-2l5-1%203-1c0-2-5-1-8%201m-16%2018c0%204-2%206-3%204s-2-2-3-1c-2%202-3%202-2%200l-1-1-4-1c-3-1-4%200-4%204%200%203%200%203-2%202-4-3-5-5-1-6l2-2-5%201c-3%202-1%208%203%208l2%202c0%203%201%203%203%200%201-3%202-3%205-3l5-1h1l6%201c5%200%206-2%201-4l-1-3-1-3c-1-1-1%200-1%203m125%2039l-1%204v15c-2%2013-2%2023-1%2023l1-7%201-18c1-6%202-12%201-13v-3h18c19%201%2036%202%2041%204h2v15c-1%2017-3%2029-4%2030h-8a413%20413%200%200011%202v-5a321%20321%200%20004-42c0-2-4-3-24-4-31-2-41-2-41-1m7%2014c0%206%200%206%205%205l7-2c3%200%204-1%205-3%201-4-2-5-10-5h-7v5m-238%208l-2%203-2%201c0-2-3-5-4-5l-4%203c-3%203-3%203-4%201%200-2-1-3-3-1s-1%205%200%203h2c1%203%204%202%206-1l2-2-1%202c0%203%202%205%205%203h5c4-1%207-4%203-5-1-1-1-1%201-2%203-1%203-2-1-2-2%200-3%201-3%202m250%205c-3%201-4%203-1%203%201-1%202-1%201%201-1%205%205%206%2020%201l1-3v-3h-10c-6-1-9%200-11%201m-251%206l-25%201c-18%201-23%201-12%202h30l19-2c11%200%2012%200%207%207-4%205-4%207-1%207%202%200%202%200%202%202-6%2019-12%2055-9%2054l1-5c0-10%204-29%209-47%201-5%201-5%207-7l6-3c2-1-1-1-5%201-4%201-5%201-4-1l-1-1-1-4%201-4-9-1-15%201m-44%2020c0%2015%200%2018-2%2028l-3%2020c0%2011%200%2011%2015%208l26-1c13%201%2024%200%2027-2s0-2-5%200l-21%201H40c-15%203-14%203-14-5l1-9%202-15c2-12%202-21%201-33-1-14-2-8-1%208m22-8l-6%202h4c10-1%2025%201%2024%204l-2%209-1%208h-5c-4%200-5%200-4-2%200-4-2-7-5-7-2%200-9%205-8%206l4-2%204-3c2%200%203%203%203%205s-1%202-8%202l-8%201c0%201%208%202%2019%202h10v-4l1-9c3-9%203-9-3-11-7-2-13-2-19-1m155%2026c-3%205-3%205%200%205h7c0-2%203-1%205%201%201%201%201%201%203-1s2-2%204-1c1%202%202%202%203%200%202-1%202-1%202%201l1%203%201-4c0-6-1-7-3-3-2%203-4%203-4%200-1-3-3-2-4%201l-2%204v-4c0-5-1-5-5-1-3%203-8%202-6-1v-2l-2%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/a619787353cf239aa5892b00e46a38eb/ca0a1/How-screenshots-workdbggjc.webp 223w", "/static/a619787353cf239aa5892b00e46a38eb/75680/How-screenshots-workdbggjc.webp 445w", "/static/a619787353cf239aa5892b00e46a38eb/d00b9/How-screenshots-workdbggjc.webp 800w"],
                "sizes": "(max-width: 800px) 100vw, 800px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/a619787353cf239aa5892b00e46a38eb/e92b6/How-screenshots-workdbggjc.png 223w", "/static/a619787353cf239aa5892b00e46a38eb/e66bf/How-screenshots-workdbggjc.png 445w", "/static/a619787353cf239aa5892b00e46a38eb/5a190/How-screenshots-workdbggjc.png 800w"],
                "sizes": "(max-width: 800px) 100vw, 800px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/a619787353cf239aa5892b00e46a38eb/5a190/How-screenshots-workdbggjc.png",
                "alt": "How screenshots work",
                "title": "How screenshots work",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`How screenshots work`}</figcaption>{`
  `}</figure></a></p>
    <h2 {...{
      "id": "what-is-prefetching"
    }}>{`What is prefetching`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`SocialCardImage`}</inlineCode>{` component works great. Slow, but great.`}</p>
    <p>{`Pop it on a page, add input from a form, users get a social card. Everyone expects a spinner. Can't load something before the user says what.`}</p>
    <p>{`What if you load this as a new page?`}</p>
    <p>{`Fill out the form, click submit, get redirected. Would you expect loading spinners like this?`}</p>
    <p><img parentName="p" {...{
        "src": "/5c9656748336fd25801d136b29e17e37/Spinners-after-load97fh34.gif",
        "alt": "Spinners after load"
      }}></img></p>
    <p>{`Or would you think `}<em parentName="p">{`"The site has data, why am I waiting twice?"`}</em></p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media0.giphy.com/media/l3nSILUHhuVDuWSnC/giphy-loop.mp4?cid=7cf28b0f1wk9goz1bp96qfki4e0o4687c7texp13hro9goez&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Granted, it's 2020 and you wouldn't expect the site to reload after a form submit. But I wanted to figure out prefetching 😇`}</p>
    <h2 {...{
      "id": "prefetching-data-with-nextjs"
    }}>{`Prefetching data with NextJS`}</h2>
    <p><a parentName="p" {...{
        "href": "https://nextjs.org/"
      }}>{`NextJS`}</a>{` offers two convenient ways to pre-define data for your page:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`getStaticProps`}</inlineCode>{` for static data`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`getServerSideProps`}</inlineCode>{` for dynamic server data`}</li>
    </ul>
    <p>{`One is meant for SSR – server side rendering – and the other for SSH – server side generation. Which is which feels unclear from the docs.`}</p>
    <p>{`Both enable you to bake data into the initial HTML for your page. The difference is when this baking happens.`}</p>
    <p>{`Both work the same way:`}</p>
    <ol>
      <li parentName="ol">{`NextJS starts building a page`}</li>
      <li parentName="ol">{`Calls your exported `}<inlineCode parentName="li">{`getStaticProps`}</inlineCode>{` or `}<inlineCode parentName="li">{`getServerSideProps`}</inlineCode>{` function`}</li>
      <li parentName="ol">{`Function does stuff`}</li>
      <li parentName="ol">{`Returns a `}<inlineCode parentName="li">{`props`}</inlineCode>{` object`}</li>
      <li parentName="ol">{`NextJS renders your page with those props`}</li>
    </ol>
    <h3 {...{
      "id": "getserversideprops-with-nextjs-dynamic-routing"
    }}>{`getServerSideProps with NextJS dynamic routing`}</h3>
    <p>{`To render the `}<inlineCode parentName="p">{`SocialCardImage`}</inlineCode>{` with a pre-defined article title based on a URL slug, you'd write a page like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// pages/[slug].js

export async function getServerSideProps(context) {
    const { slug } = context.params

    const article = magicallyReadArticle(slug)

    return {
        props: {
            article
        }
    }
}

export default ArticlePreview({ article }) {
    return <SocialCardImage title={article.title} />
}
`}</code></pre>
    <p>{`Naming your file `}<inlineCode parentName="p">{`[slug]`}</inlineCode>{` gives you `}<a parentName="p" {...{
        "href": "https://nextjs.org/docs/routing/dynamic-routes"
      }}>{`nextjs dynamic routing`}</a>{` – file runs for any `}<inlineCode parentName="p">{`/this-is-slug`}</inlineCode>{` URL and renders the `}<inlineCode parentName="p">{`ArticlePreview`}</inlineCode>{` page component.`}</p>
    <p>{`NextJS runs `}<inlineCode parentName="p">{`getServerSideProps`}</inlineCode>{` with the routing context and you use the slug to find your article in a storage system. We used temp files but that doesn't work in production.`}</p>
    <p>{`The function returns your `}<inlineCode parentName="p">{`article`}</inlineCode>{` as a prop, it gets passed into the page component, and the user gets a preview.`}</p>
    <p>{`And they get a spinner.`}</p>
    <p><img parentName="p" {...{
        "src": "/5c9656748336fd25801d136b29e17e37/Spinners-after-load46cj0g.gif",
        "alt": "Spinners after load"
      }}></img></p>
    <h3 {...{
      "id": "add-react-query-to-the-mix"
    }}>{`Add React Query to the mix`}</h3>
    <p>{`You can fix the spinner by manually fetching the social card in `}<inlineCode parentName="p">{`getServerSideProps`}</inlineCode>{`. And that means duplicating your logic, dealing with checking 2 data sources, etc.`}</p>
    <p>{`You've built the query before. You've got a component that works. Why rebuild it all?? 🤨`}</p>
    <p>{`Here's what you do instead:`}</p>
    <ol>
      <li parentName="ol">{`Wrap your app in React Query cache`}</li>
      <li parentName="ol">{`Prefetch query in `}<inlineCode parentName="li">{`getServerSideProps`}</inlineCode></li>
      <li parentName="ol">{`Dehydrate the full cache on page load`}</li>
      <li parentName="ol">{`Keep frontend code the same`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// pages/_app.js

const queryCache = new QueryCache()

function MyApp({ Component, pageProps }) {
  return (
    <ReactQueryCacheProvider queryCache={queryCache}>
      <Hydrate state={pageProps.dehydratedState}>
        <ThemeProvider theme={theme}>
          <Component {...pageProps} />
        </ThemeProvider>
      </Hydrate>
    </ReactQueryCacheProvider>
  )
}
`}</code></pre>
    <p>{`Create a new query cache, wrap the app in `}<inlineCode parentName="p">{`<ReactQueryCacheProvider>`}</inlineCode>{` and `}<inlineCode parentName="p">{`<Hydrate>`}</inlineCode>{`. They're react-query's context providers for internal state sharing.`}</p>
    <p>{`Then prefetch your queries:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function prefetchQueries(article) {
  const queryCache = new QueryCache()
  await queryCache.prefetchQuery(
    ["social-card", article.title],
    fetchSocialCard
  )

  return dehydrate(queryCache)
}

export async function getServerSideProps(context) {
  const { slug } = context.params

  const article = magicallyReadArticle(slug)

  return {
    props: {
      dehydratedState: await prefetchQueries(article),
      article,
    },
  }
}
`}</code></pre>
    <p>{`We can't reuse `}<inlineCode parentName="p">{`useSocialCardQuery`}</inlineCode>{` because we're doing something different – prefetching.`}</p>
    <p>{`Replace `}<inlineCode parentName="p">{`useQuery`}</inlineCode>{` with `}<inlineCode parentName="p">{`queryCache.prefetchQuery`}</inlineCode>{` and pass that into the `}<inlineCode parentName="p">{`dehydrate()`}</inlineCode>{` call from react-query. Put the full result in the `}<inlineCode parentName="p">{`dehydratedState`}</inlineCode>{` prop.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`<Hydrate>`}</inlineCode>{` component reads that from its props`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<Hydrate state={pageProps.dehydratedState}>
`}</code></pre>
    <p>{`And you get a page load with no spinners`}</p>
    <p><img parentName="p" {...{
        "src": "/87603c837204363cdde9d405c1ab56c8/Page-load-with-social-card-without-spinners63744d.gif",
        "alt": "Page load with social card without spinners"
      }}></img></p>
    <p>{`Okay the UX around that form submit needs work but you get the idea. No spinners! 🎉`}</p>
    <h2 {...{
      "id": "why-i-like-this"
    }}>{`Why I like this`}</h2>
    <p>{`Way easier to use than Gatsby's `}<inlineCode parentName="p">{`useStaticQuery`}</inlineCode>{`. Same code works with both static data `}<em parentName="p">{`and`}</em>{` dynamic updates later.`}</p>
    <p>{`Data baking in Gatsby is more suited to pages that don't turn into single-page-apps. This works for both 😍`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      